import ErrorMessage from 'components/auth/error-message'
import { useField } from 'formik'
import classNames from 'helpers/class-names'
import { uniqueId } from 'lodash'
import { FC, HTMLInputTypeAttribute, HTMLProps } from 'react'

type InputProps = HTMLProps<HTMLInputElement> & {
  className?: string
  disabled?: boolean
  id?: string
  name: string
  type?: HTMLInputTypeAttribute
  prefix?: string
  suffix?: string
  placeholder?: string
  inputWrapperClassName?: string
  inputClassName?: string
  variant?: 'compact' | 'normal' | 'large'
  textEnd?: boolean
}

export const FormikInput: FC<InputProps> = ({
  className,
  disabled,
  id = uniqueId('i'),
  name,
  type = 'text',
  prefix,
  suffix,
  placeholder,
  inputWrapperClassName,
  inputClassName,
  variant = 'normal',
  textEnd = false,
  ...rest
}) => {
  const [field, meta] = useField({ name, id })
  const showError = Boolean(meta.touched && meta.error)

  return (
    <div className={classNames('block w-full font-semibold', className)}>
      <div
        className={classNames(
          'relative flex w-full flex-nowrap items-stretch',
          'border border-gray-100 bg-white',
          'focus-within:border-info focus-within:ring-2 focus-within:ring-info/50',
          'dark:border-gray-700 dark:bg-gray-500',
          {
            'border-danger ring-2 ring-danger/50': showError,
            'bg-gray-50 dark:bg-gray-700': disabled,
          },
          {
            'h-6 rounded text-xs': variant === 'compact',
            'h-7 rounded-md text-sm': variant === 'normal',
            'h-9 rounded-md text-lg': variant === 'large',
          },
          inputWrapperClassName,
        )}>
        {prefix && (
          <div
            className={classNames(
              'flex items-center  bg-gray-200  text-white dark:bg-gray-600',
              {
                'px-1': variant === 'compact',
                'rounded-l px-1.5': variant === 'normal',
                'rounded-l px-2': variant === 'large',
              },
            )}>
            {prefix}
          </div>
        )}
        <input
          disabled={disabled}
          aria-labelledby={id}
          type={type}
          name={name}
          placeholder={placeholder}
          className={classNames(
            'w-px flex-1 grow bg-transparent px-2 leading-normal text-gray-400',
            'placeholder-gray-200 outline-none',
            'dark:text-gray-100 dark:placeholder-gray-300',
            {
              'cursor-not-allowed select-none text-gray-200 dark:text-gray-400':
                disabled,
            },
            {
              'appearance-none': type === 'number',
            },
            { 'text-end': textEnd },
            {
              'px-1': variant === 'compact',
              'pl-2 pr-1.5': variant === 'normal',
              'px-2': variant === 'large',
            },
            inputClassName,
          )}
          {...field}
          {...rest}
        />
        {suffix && (
          <div
            className={classNames(
              'flex items-center  border-l border-gray-200 bg-gray-200 text-white dark:border-gray-700 dark:bg-gray-600',
              {
                'px-1 ': variant === 'compact',
                'rounded-r px-1.5 ': variant === 'normal',
                'rounded-r px-2': variant === 'large',
              },
            )}>
            {suffix}
          </div>
        )}
      </div>

      {/* passing className prop with classNames() and variant prop didnt work */}
      {variant === 'normal' && (
        <ErrorMessage isVisible={showError} className="px-1.5 py-0.5 text-xs">
          {meta.error}
        </ErrorMessage>
      )}
      {variant === 'large' && (
        <ErrorMessage isVisible={showError} className="px-2 py-1.5 text-sm">
          {meta.error}
        </ErrorMessage>
      )}
    </div>
  )
}
